import { createNodeRuleSet, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadHoleType, FootPrintPadShape, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, pinMarkerMargin, pinMarkerRadius, silkWidth } from "./commonConstants";
/**
 * SOT package configurations
 * All dimensions in millimeters (will be converted to meters)
 * rowSpacing is the center-to-center distance between pads on opposite sides
 * Based on typical recommended land patterns from datasheets
 */
export const sotPackageConfigs = {
    "SOT-23": {
        pinCount: 3,
        pinPitch: 0.95,
        pinSizeX: 0.6,
        pinSizeY: 1.0,
        packageWidth: 2.9,
        packageHeight: 1.3,
        packageDepth: 1.0,
        rowSpacing: 2.6, // Updated: typical land pattern is 2.6mm center-to-center
        pinsPerSide: [2, 1], // 2 pins on side 0, 1 pin on side 1
    },
    "SOT-23-5": {
        pinCount: 5,
        pinPitch: 0.95,
        pinSizeX: 0.6,
        pinSizeY: 1.0,
        packageWidth: 2.9,
        packageHeight: 1.6,
        packageDepth: 1.0,
        rowSpacing: 2.6, // Updated: typical land pattern is 2.6mm center-to-center
        pinsPerSide: [3, 2], // 3 pins on side 0, 2 pins on side 1
    },
    "SOT-23-6": {
        pinCount: 6,
        pinPitch: 0.95,
        pinSizeX: 0.6,
        pinSizeY: 1.0,
        packageWidth: 2.9,
        packageHeight: 1.6,
        packageDepth: 1.0,
        rowSpacing: 2.6, // Updated: typical land pattern is 2.6mm center-to-center
        pinsPerSide: [3, 3], // 3 pins on each side
    },
    "SOT-89": {
        pinCount: 3,
        pinPitch: 1.5,
        pinSizeX: 0.7,
        pinSizeY: 1.5,
        packageWidth: 4.5,
        packageHeight: 2.5,
        packageDepth: 1.5,
        rowSpacing: 4.0, // Updated: typical land pattern for SOT-89
        pinsPerSide: [3, 0], // 3 pins on one side, tab on other
        tabSizeX: 2.0, // Thermal tab width
        tabSizeY: 1.5, // Thermal tab height
    },
    "SOT-223": {
        pinCount: 4,
        pinPitch: 2.3,
        pinSizeX: 0.9,
        pinSizeY: 1.5,
        packageWidth: 6.5,
        packageHeight: 3.5,
        packageDepth: 1.6,
        rowSpacing: 6.5, // Updated: typical land pattern for SOT-223
        pinsPerSide: [3, 1], // 3 pins on side 0, 1 tab on side 1
        tabSizeX: 3.0, // Thermal tab width (much larger than regular pins)
        tabSizeY: 2.0, // Thermal tab height
    },
    "TSOT-23": {
        pinCount: 3,
        pinPitch: 0.65,
        pinSizeX: 0.4,
        pinSizeY: 0.6,
        packageWidth: 2.1,
        packageHeight: 1.25,
        packageDepth: 0.9,
        rowSpacing: 2.0, // Updated: typical land pattern for TSOT-23
        pinsPerSide: [2, 1],
    },
    "TSOT-23-5": {
        pinCount: 5,
        pinPitch: 0.65,
        pinSizeX: 0.4,
        pinSizeY: 0.6,
        packageWidth: 2.1,
        packageHeight: 1.25,
        packageDepth: 0.9,
        rowSpacing: 2.0, // Updated: typical land pattern for TSOT-23
        pinsPerSide: [3, 2],
    },
    "TSOT-23-6": {
        pinCount: 6,
        pinPitch: 0.65,
        pinSizeX: 0.4,
        pinSizeY: 0.6,
        packageWidth: 2.1,
        packageHeight: 1.25,
        packageDepth: 0.9,
        rowSpacing: 2.0, // Updated: typical land pattern for TSOT-23
        pinsPerSide: [3, 3],
    },
};
/**
 * Get SOT package config from package name
 */
export function getSotConfig(packageName) {
    return sotPackageConfigs[packageName];
}
function sotInferPinLayout(bakedRules, existingPadNodes, pinsPerSide) {
    const result = { grid: [], otherPads: [] };
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => pad.terminalId === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const remainingPadsWithNumbers = existingPadNodes.filter((pad) => pad.terminalId !== "" && pad.terminalId !== undefined);
    let pinIndex = 1;
    // Process each side
    for (let sideId = 0; sideId < pinsPerSide.length; sideId++) {
        const pinsOnSide = pinsPerSide[sideId] ?? 0;
        const sideArr = [];
        for (let i = 0; i < pinsOnSide; i++) {
            const foundMatchingI = remainingPadsWithNumbers.findIndex((pad) => pad.terminalId === `${pinIndex}`);
            const foundMatching = remainingPadsWithNumbers[foundMatchingI];
            if (foundMatchingI !== -1) {
                remainingPadsWithNumbers.splice(foundMatchingI, 1);
            }
            const matchingPad = foundMatching ?? withoutPinNumberPadNodes.shift();
            sideArr.push(matchingPad ?? null);
            pinIndex++;
        }
        result.grid[sideId] = sideArr;
    }
    result.otherPads.push(...withoutPinNumberPadNodes);
    result.otherPads.push(...remainingPadsWithNumbers);
    return result;
}
export function sotComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSizeX, y: config.pinSizeY });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.pinPitch, y: config.rowSpacing });
    const padOuterMargin = replaceAutoWithDefault2(bakedRules.padOuterMargin, {
        x: config.padOuterMargin ?? 0.3 / 1000,
        y: config.padOuterMargin ?? 0.3 / 1000,
    });
    const maxPinsPerSide = Math.max(...config.pinsPerSide);
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: maxPinsPerSide,
        y: config.pinsPerSide.length,
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        x: config.packageWidth,
        y: config.packageDepth,
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
        padOuterMargin,
    };
    return { ...newBakedRules, pinLayout: sotInferPinLayout(newBakedRules, existingPadNodes, config.pinsPerSide) };
}
export function sotGeneratePadRules(footprintNodeUid, bakedRules, pinsPerSide, thermalTabConfig) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const rowSpacing = bakedRules.pinPitch?.y ?? 0;
    const padOuterMargin = bakedRules.padOuterMargin?.x ?? 0;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result;
    for (let sideId = 0; sideId < pinLayout.grid.length; sideId++) {
        const sideArr = pinLayout.grid[sideId];
        if (!sideArr)
            continue;
        const pinsOnSide = pinsPerSide[sideId] ?? 0;
        const totalWidth = (pinsOnSide - 1) * pinPitchX;
        // Check if this side has a thermal tab (single pin on one side with thermal config)
        const isThermalTab = thermalTabConfig && pinsOnSide === 1;
        for (let i = 0; i < sideArr.length; i++) {
            const matchingPad = sideArr[i];
            if (!matchingPad)
                continue;
            let posX = 0;
            let posY = 0;
            let rotZ = 0;
            if (sideId === 0) {
                // Left side
                posX = -rowSpacing / 2;
                posY = -(i * pinPitchX - totalWidth / 2);
                rotZ = 270;
            }
            else if (sideId === 1) {
                // Right side
                posX = rowSpacing / 2;
                posY = i * pinPitchX - totalWidth / 2;
                rotZ = 90;
            }
            // Use thermal tab size if this is a thermal tab, otherwise use regular pin size
            const padSizeX = isThermalTab ? thermalTabConfig.tabSizeX : pinSizeX;
            const padSizeY = isThermalTab ? thermalTabConfig.tabSizeY : pinSizeY;
            const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
            const ruleSet = {
                uid: matchingPad.uid,
                selector: selector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            result[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                size: `${metersToMm(padSizeX)}mm ${metersToMm(padSizeY + padOuterMargin)}mm`,
                rotationZ: `${rotZ}deg`,
                padShape: FootPrintPadShape.rectangle,
                padHoleType: FootPrintPadHoleType.SurfaceMountDevice,
            };
            ruleSet.rules = createNodeRuleSet(rules);
        }
    }
    return result;
}
export function sotGenerateAdditionalPads(_footprintNodeUid, _bakedRules) {
    return [];
}
export function sotGenerateSilkNodes(footprintNodeUid, bakedRules, pinsPerSide) {
    if (!bakedRules.packageSize)
        return [];
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const padOuterMargin = bakedRules.padOuterMargin?.x ?? 0;
    const pinsOnSide0 = pinsPerSide[0] ?? 0;
    const totalWidth0 = (pinsOnSide0 - 1) * pinPitchX;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    const packageWidth = bakedRules.packageSize.x;
    const packageHeight = bakedRules.packageSize.y;
    // Draw package outline
    silkPaths.push(`<path d="
              M ${-packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${packageHeight / 2}
              L ${-packageWidth / 2} ${packageHeight / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    // Pin 1 marker
    const pin1Y = -totalWidth0 / 2;
    silkPaths.push(`<circle
              cx="${-packageWidth / 2 - pinMarkerMargin - padOuterMargin}"
              cy="${pin1Y}"
              r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(packageHeight / 2 + designatorMargin + padOuterMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
//# sourceMappingURL=sot.js.map